import { createContext, useContext, useState, useCallback, useRef } from "react";
import {
  updatePlaceThumbnail,
  updatePlaceSeasonMedia,
  updatePlacePhotoAuthors,
} from "../services/placesService";
import { uploadToCloudinary } from "../utils/uploadToCloudinary";

const UploadQueueContext = createContext(null);

const CONCURRENCY = 4;

async function uploadFiles(files, onProgress) {
  const progresses = new Array(files.length).fill(0);
  const updateOverall = () => {
    const overall = Math.round(
      progresses.reduce((sum, p) => sum + p, 0) / files.length,
    );
    onProgress(overall);
  };
  const urls = new Array(files.length);
  let nextIndex = 0;
  const runWorker = async () => {
    while (nextIndex < files.length) {
      const i = nextIndex++;
      urls[i] = await uploadToCloudinary(files[i], (p) => {
        progresses[i] = p;
        updateOverall();
      });
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, files.length) }, runWorker),
  );
  return urls;
}

export function UploadQueueProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const [overrides, setOverrides] = useState({});
  const jobIdRef = useRef(0);

  const patchOverride = useCallback((placeId, patch) => {
    setOverrides((prev) => ({
      ...prev,
      [placeId]: { ...(prev[placeId] || {}), ...patch },
    }));
  }, []);

  const mergePlace = useCallback(
    (place) => (place ? { ...place, ...(overrides[place.id] || {}) } : place),
    [overrides],
  );

  const addJob = (job) => {
    const id = ++jobIdRef.current;
    setJobs((prev) => [...prev, { ...job, id, progress: 0, status: "uploading" }]);
    return id;
  };
  const updateJob = (id, patch) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
  };
  const removeJob = (id) => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  };
  const finishJob = (id) => {
    updateJob(id, { status: "done", progress: 100 });
    setTimeout(() => removeJob(id), 2000);
  };
  const failJob = (id) => {
    updateJob(id, { status: "error" });
    setTimeout(() => removeJob(id), 4000);
  };

  const uploadThumbnail = useCallback(
    (place, file) => {
      const jobId = addJob({
        placeId: place.id,
        placeName: place.name,
        label: "Thumbnail",
      });
      (async () => {
        try {
          const [url] = await uploadFiles([file], (p) =>
            updateJob(jobId, { progress: p }),
          );
          await updatePlaceThumbnail(place.id, url);
          patchOverride(place.id, { image: url });
          finishJob(jobId);
        } catch (err) {
          console.error("Thumbnail upload error:", err);
          failJob(jobId);
        }
      })();
    },
    [patchOverride],
  );

  const setThumbnailFromUrl = useCallback(
    async (place, url) => {
      await updatePlaceThumbnail(place.id, url);
      patchOverride(place.id, { image: url });
    },
    [patchOverride],
  );

  const uploadSeasonMedia = useCallback(
    (place, mediaType, season, files, seasonLabel) => {
      const jobId = addJob({
        placeId: place.id,
        placeName: place.name,
        label: `${seasonLabel} ${mediaType === "image" ? "Photos" : "Videos"}`,
      });
      (async () => {
        try {
          const field = mediaType === "image" ? "images" : "videos";
          const seasonField =
            field === "images" ? "imagesBySeason" : "videosBySeason";
          const current = mergePlace(place);
          const existing = current[seasonField]?.[season] || [];
          const urls = await uploadFiles(files, (p) =>
            updateJob(jobId, { progress: p }),
          );
          const updated = [...existing, ...urls];
          await updatePlaceSeasonMedia(place.id, field, season, updated);
          patchOverride(place.id, {
            [seasonField]: {
              ...(current[seasonField] || {}),
              [season]: updated,
            },
          });
          finishJob(jobId);
        } catch (err) {
          console.error("Media upload error:", err);
          failJob(jobId);
        }
      })();
    },
    [mergePlace, patchOverride],
  );

  const deleteSeasonMedia = useCallback(
    async (place, mediaType, season, url) => {
      const field = mediaType === "image" ? "images" : "videos";
      const seasonField =
        field === "images" ? "imagesBySeason" : "videosBySeason";
      const current = mergePlace(place);
      const existing = current[seasonField]?.[season] || [];
      const updated = existing.filter((u) => u !== url);
      await updatePlaceSeasonMedia(place.id, field, season, updated);
      patchOverride(place.id, {
        [seasonField]: { ...(current[seasonField] || {}), [season]: updated },
      });
      const existingAuthors = current.photoAuthors || [];
      if (existingAuthors.some((a) => a.url === url)) {
        const updatedAuthors = existingAuthors.filter((a) => a.url !== url);
        await updatePlacePhotoAuthors(place.id, updatedAuthors);
        patchOverride(place.id, { photoAuthors: updatedAuthors });
      }
    },
    [mergePlace, patchOverride],
  );

  const tagPhotoAuthors = useCallback(
    async (place, urls, author) => {
      const current = mergePlace(place);
      const existing = current.photoAuthors || [];
      const filtered = existing.filter((a) => !urls.includes(a.url));
      const updated = author
        ? [
            ...filtered,
            ...urls.map((url) => ({
              url,
              name: author.name,
              surname: author.surname,
            })),
          ]
        : filtered;
      await updatePlacePhotoAuthors(place.id, updated);
      patchOverride(place.id, { photoAuthors: updated });
    },
    [mergePlace, patchOverride],
  );

  return (
    <UploadQueueContext.Provider
      value={{
        jobs,
        mergePlace,
        uploadThumbnail,
        setThumbnailFromUrl,
        uploadSeasonMedia,
        deleteSeasonMedia,
        tagPhotoAuthors,
      }}
    >
      {children}
    </UploadQueueContext.Provider>
  );
}

export function useUploadQueue() {
  return useContext(UploadQueueContext);
}
